import React, { useState, useContext, useEffect, useRef } from "react";
import "./styles/styles.css";
import "../../styles/global.css";
import { useNavigate } from "react-router-dom";
import { icons, images } from "../../constants";
import { TranslationsContext } from "../../utils/TranslationsContext";
import Languages from "./components/Languages";
import Themes from "./components/Themes";
import { handleLogout } from "../../utils/methods";

// Sidebar expanded/collapsed preference. New key: the old "expandableNavbar"
// got "false" written on every load (and on any narrow window), so it never
// reflected an actual choice — this one is only written when the user
// toggles on desktop, and missing means expanded.
const SIDEBAR_EXPANDED_KEY = "sidebarExpanded";

const NavbarSingle = ({ setNavWidth, setLanguage }) => {
  // Same expand/collapse as NavBar.jsx (desktop only), sharing its saved
  // preference so both navbars open the same way.
  const [expandableNavbar, setExpandableNavbar] = useState(() => {
    // Expanded by default (no saved choice yet); a collapse the user made
    // is remembered. Desktop only — narrower screens use the top bar.
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved !== "false" && window.innerWidth >= 1000;
  });
  const [isToggleHovered, setIsToggleHovered] = useState(false);
  const [isBarOpen, setIsBarOpen] = useState(false);
  const navRef = useRef();
  const [openLanguages, setOpenLanguages] = useState(false);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light",
  );
  const navigate = useNavigate();
  const handleLogoutClick = async () => {
    localStorage.removeItem("single"); // Clear single flag
    await handleLogout();
  };
  const { t } = useContext(TranslationsContext);

  useEffect(() => {
    const updateNavWidth = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth < 1000) {
        setNavWidth(0);
      } else if (navRef.current) {
        requestAnimationFrame(() => {
          if (navRef.current) {
            setNavWidth(navRef.current.offsetWidth);
          }
        });
      }
    };

    updateNavWidth();
    window.addEventListener("resize", updateNavWidth);

    return () => {
      window.removeEventListener("resize", updateNavWidth);
    };
  }, [setNavWidth, expandableNavbar]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1000) {
        setExpandableNavbar(false);
      } else {
        // Back on desktop: return to the saved preference (default expanded).
        setExpandableNavbar(
          localStorage.getItem(SIDEBAR_EXPANDED_KEY) !== "false",
        );
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth >= 1000) {
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, expandableNavbar.toString());
    }
  }, [expandableNavbar]);

  return (
    <div
      className={`flex-direction-column items-center ${
        expandableNavbar ? "expandable" : ""
      } navbar single`}
      ref={navRef}
    >
      <div className="flex-direction-row items-center width-100 navbar-logo-hamburger">
        <img src={images.emblemMenu} className="navbar-logo-img" alt="" />
        <div
          className={`navbar-toggle-tab ${expandableNavbar ? "expanded" : ""}`}
          onClick={() => {
            if (window.innerWidth >= 1000) {
              setExpandableNavbar(!expandableNavbar);
            }
          }}
          onMouseEnter={() => setIsToggleHovered(true)}
          onMouseLeave={() => setIsToggleHovered(false)}
        >
          <img
            src={
              isToggleHovered
                ? expandableNavbar
                  ? icons.menu_arrow_left_blue
                  : icons.menu_arrow_right_blue
                : theme === "dark"
                  ? expandableNavbar
                    ? icons.menu_arrow_left_dark
                    : icons.menu_arrow_right_dark
                  : expandableNavbar
                    ? icons.menu_arrow_left
                    : icons.menu_arrow_right
            }
            className="navbar-toggle-arrow"
            alt="toggle menu"
          />
        </div>
        <div className="flex-direction-row items-center gap-18 language-hamburger">
          <div className="flex-direction-row items-center gap-2">
            <div className="navbar-themes-under-999">
              <Themes
                setIsBarOpen={(value) => setIsBarOpen(value)}
                changeThemeInNavbar={setTheme}
              />
            </div>
            <div
              className="navbar-languages-under-999"
              // onClick={() => setOpenLanguages(!openLanguages)}
            >
              <Languages
                openLanguages={openLanguages}
                setIsBarOpen={(value) => setIsBarOpen(value)}
                setLanguageGeneral={setLanguage}
                setOpenLanguages={setOpenLanguages}
                small={true}
              />
            </div>
          </div>
          <img
            className="hamburger"
            src={isBarOpen ? icons.close : icons.hamburger}
            onClick={() => setIsBarOpen(!isBarOpen)}
            width={30}
            height={30}
            alt="X"
          />
        </div>
      </div>
      <div
        className={`flex-direction-column gap-96 justify-between width-100 height-100 navbar-menu single ${
          isBarOpen ? "open" : ""
        } `}
      >
        <div className="flex-direction-column navbar-links">
          <div
            className="flex-directions-row items-center pointer navbar-link"
            onClick={() => navigate("/")}
          >
            <img
              src={theme == "light" ? icons.calendar : icons.calendar_dark}
              width={24}
              height={24}
              alt="events"
            />
            <p className="inria-sans-300 font-14 weight-700 navbar-term">
              {t("EVENTS_TITLE")}
            </p>
          </div>
        </div>
        <div className="flex-direction-column gap-18  navbar-settings">
          <div className="navbar-themes">
            <Themes
              setIsBarOpen={(value) => setIsBarOpen(value)}
              changeThemeInNavbar={setTheme}
            />
          </div>
          <div className="navbar-languages">
            <Languages
              openLanguages={openLanguages}
              setOpenLanguages={setOpenLanguages}
              setIsBarOpen={(value) => setIsBarOpen(value)}
              setLanguageGeneral={setLanguage}
              expanded={expandableNavbar}
            />
          </div>
          <div
            className="flex-directions-row items-center pointer navbar-link"
            onClick={handleLogoutClick}
          >
            <img
              src={theme == "light" ? icons.logout : icons.logout_dark}
              width={24}
              height={24}
              alt="X"
            />
            <p className="inria-sans-300 font-14 weight-700 navbar-term">
              {t("LOGOUT")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavbarSingle;
